// API client - all backend communication is centralized here
// Frontend developers should not need to modify this file

import type {
  HashResult,
  SigningStatus,
  VerifyResult,
  EmailVerificationResult,
} from "../types";

/**
 * Safely parse JSON from a response, returning null if empty or invalid
 */
async function safeParseJson<T>(response: Response): Promise<T | null> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

/**
 * Check if email notifications are available (SMTP configured)
 */
export async function checkEmailStatus(): Promise<{ configured: boolean }> {
  try {
    const response = await fetch("/api/email/status");
    if (response.ok) {
      return await response.json();
    }
    return { configured: false };
  } catch {
    return { configured: false };
  }
}

/**
 * Hash an image and get perceptual hashes
 */
export async function hashImage(
  imageData: ArrayBuffer,
  contentType: string
): Promise<HashResult> {
  const response = await fetch("/api/hash", {
    method: "POST",
    headers: {
      "Content-Type": contentType,
    },
    body: imageData,
  });

  if (!response.ok) {
    const data = await safeParseJson<{ error?: string }>(response);
    throw new Error(data?.error || `Failed to hash image (${response.status})`);
  }

  return await response.json();
}

/**
 * Initiate signing request with ZK Email
 */
export async function signHash(params: {
  email: string;
  hash: string;
  hash96?: string;
  enableZk: boolean;
  registerOnChain: boolean;
  sendEmailNotification?: boolean;
  image?: File;
}): Promise<{ success: boolean; id?: string; emailNotificationEnabled?: boolean }> {
  let response: Response;

  // If email notification is enabled and we have the image, send FormData
  if (params.sendEmailNotification && params.image) {
    const formData = new FormData();
    formData.append("email", params.email);
    formData.append("hash", params.hash);
    if (params.hash96) {
      formData.append("hash96", params.hash96);
    }
    formData.append("enableZk", params.enableZk ? "true" : "false");
    formData.append(
      "registerOnChain",
      params.enableZk && params.registerOnChain ? "true" : "false"
    );
    formData.append("sendEmailNotification", "true");
    formData.append("image", params.image);

    response = await fetch("/api/sign", {
      method: "POST",
      body: formData,
    });
  } else {
    // Standard JSON request
    response = await fetch("/api/sign", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: params.email,
        hash: params.hash,
        enableZk: params.enableZk,
        registerOnChain: params.enableZk ? params.registerOnChain : false,
      }),
    });
  }

  if (!response.ok) {
    const data = await safeParseJson<{ error?: string }>(response);
    throw new Error(
      data?.error || `Failed to initiate signing (${response.status})`
    );
  }

  return await response.json();
}

/**
 * Get status of a signing request
 */
export async function getSigningStatus(
  requestId: string
): Promise<SigningStatus | null> {
  try {
    const response = await fetch(`/api/sign/${encodeURIComponent(requestId)}`);
    if (!response.ok) {
      return null;
    }
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Extract metadata and verify an image
 */
export async function verifyImage(
  imageData: ArrayBuffer,
  contentType: string
): Promise<VerifyResult> {
  const response = await fetch("/api/extract", {
    method: "POST",
    headers: {
      "Content-Type": contentType,
    },
    body: imageData,
  });

  if (!response.ok) {
    const data = await safeParseJson<{ error?: string }>(response);
    throw new Error(
      data?.error || `Failed to verify image (${response.status})`
    );
  }

  return await response.json();
}

/**
 * Verify a raw email using DKIM
 */
export async function verifyEmail(
  rawEmail: string
): Promise<EmailVerificationResult> {
  const response = await fetch("/api/verify-email", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rawEmail }),
  });

  return await response.json();
}

/**
 * Embed metadata into an image and get as blob for download
 */
export async function embedMetadata(params: {
  image: File;
  hash96: string;
  hash512: string;
  rawEmail?: string;
  proof?: object;
}): Promise<{ blob: Blob; filename: string }> {
  const formData = new FormData();
  formData.append("image", params.image);
  formData.append("hash96", params.hash96);
  formData.append("hash512", params.hash512);

  if (params.rawEmail) {
    formData.append("raw_email", params.rawEmail);
  }
  if (params.proof) {
    formData.append("proof", JSON.stringify(params.proof));
  }

  const response = await fetch("/api/embed", {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    const text = await response.text();
    let errorMessage = `Failed to embed metadata (${response.status})`;
    try {
      const data = JSON.parse(text);
      if (data?.error) errorMessage = data.error;
    } catch {
      // Not JSON, use default error
    }
    throw new Error(errorMessage);
  }

  const blob = await response.blob();
  const filename =
    response.headers
      .get("Content-Disposition")
      ?.split("filename=")[1]
      ?.replace(/"/g, "") || "signed-image";

  return { blob, filename };
}

/**
 * Download a blob as a file
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
